import { useCallback, useMemo, useRef, useState } from 'react';
import {
  MENU_TEST_ID,
  RECIPIENT_MENU_MAX_HEIGHT,
  RECIPIENT_MENU_MIN_HEIGHT,
  RecipientPickerMenu,
} from './RecipientPickerMenu';
import {
  MAX_CC_BCC_RECIPIENTS,
  MAX_TO_RECIPIENTS,
} from '@/constants/internal-email-recipients';
import { RecipientChip, type RecipientChipOption } from './RecipientChip';
import { buildCostModel } from './recipientLimits';
import {
  filterByLabel,
  useRecipientSections,
  useRecipientUsers,
  type RecipientOption,
  type RecipientSection,
} from './useRecipientSections';

type SeenOption = {
  value: string;
  label: string;
  image?: string;
  tags?: string[];
};

/**
 * Wires the sectioned picker onto kula's To/Cc/Bcc selects.
 *
 * Returns props to spread on each of the three, so all of them share one set of
 * sections and one user query — searching in Cc does not re-fetch for Bcc.
 *
 * The menu replaces react-select's own, which is why this goes through
 * `reactSelectProps`: kula manages `options`/`value`/`onChange` on those selects
 * and spreads this escape hatch last, so `components.Menu` wins.
 */
type RecipientPickerOptions = {
  /**
   * List the job's people in place of its role tokens.
   *
   * Only a surface that addresses the email NOW may turn this on. A surface
   * that CONFIGURES one for later must keep the tokens: the activity is stored
   * as roles so it follows the hiring team as it changes, and a stored user id
   * would freeze today's team into every future send.
   */
  expandJobRolesToPeople?: boolean;
  /**
   * Grey out rows once a field has reached the cap the send is refused on.
   *
   * Off by default, and for the same reason as above: a surface that CONFIGURES
   * a send resolves no roles and often has no job, so a count there would be a
   * guess presented as a rule.
   */
  enforceLimits?: boolean;
  /**
   * The caps, as the API served them on the composer's prefill. Left out, the
   * constants stand in — they mirror the same Rails values, and an older payload
   * without the field must not disable the whole picker.
   */
  limits?: { to?: number; ccBcc?: number };
};

export type RecipientField = 'to' | 'cc' | 'bcc';

export interface RecipientSelection {
  to: string[];
  cc: string[];
  bcc: string[];
}

export const useRecipientPicker = (
  jobId?: string,
  menuPortalTarget?: HTMLElement | null,
  {
    expandJobRolesToPeople = false,
    enforceLimits = false,
    limits = {},
  }: RecipientPickerOptions = {}
) => {
  const [query, setQuery] = useState('');
  const { jobRoleOptions, jobRoleUserOptions, orgRoleOptions, tagsByUserId } =
    useRecipientSections(jobId);
  const users = useRecipientUsers(query, tagsByUserId);

  // Every option the picker has rendered. A chip for a user outside the first
  // page has no other way to resolve its label once the menu closes.
  const seenRef = useRef(new Map<string, SeenOption>());
  const remember = useCallback((options: RecipientOption[]) => {
    options.forEach((option) => {
      seenRef.current.set(option.value, {
        value: option.value,
        label: option.label,
        image: option.image,
        // Carried so a chip can name the role that put this person on the job,
        // which the label alone never says.
        tags: option.tags,
      });
    });
    return options;
  }, []);

  const searching = Boolean(query.trim());

  /**
   * A job's roles resolve to its people, but only where that is what gets sent.
   *
   * Two things have to be true: a job is known (Settings composes against none,
   * so there is no hiring team to resolve against), and the caller is composing
   * an email rather than configuring one. The interview plan configures — its
   * activity stores role tokens and resolves them per candidate at send time —
   * so it keeps the tokens, and a role removed there can be picked again.
   */
  const resolvesToPeople = Boolean(jobId) && expandJobRolesToPeople;
  const jobRoleSource = resolvesToPeople ? jobRoleUserOptions : jobRoleOptions;

  const sections = useMemo<RecipientSection[]>(() => {
    const jobRoles = filterByLabel(jobRoleSource, query);
    const orgRoles = filterByLabel(orgRoleOptions, query);
    remember(jobRoles);
    remember(orgRoles);
    remember(users.options);
    return [
      {
        id: 'job_roles',
        title: 'Job roles',
        // The matches while searching, the whole category otherwise — the badge
        // counts what the section is showing, as the users badge already did.
        // No badge at all once the rows are people: the design counts roles, and
        // a person can appear on more than one row.
        count: resolvesToPeople
          ? undefined
          : searching
            ? jobRoles.length
            : jobRoleSource.length,
        options: jobRoles,
      },
      {
        id: 'kula_roles',
        title: 'Kula roles',
        count: searching ? orgRoles.length : orgRoleOptions.length,
        options: orgRoles,
      },
      {
        id: 'users',
        title: 'Users',
        count: users.count,
        options: users.options,
        isLoading: users.isFetching,
        isReloading: users.isReloading,
        hasMore: users.hasMore,
      },
    ];
  }, [
    jobRoleSource,
    resolvesToPeople,
    orgRoleOptions,
    users.options,
    users.count,
    users.isFetching,
    users.isReloading,
    users.hasMore,
    query,
    searching,
    remember,
  ]);

  const selectProps = useMemo(
    () => ({
      // `componentsOverride`, NOT `reactSelectProps.components`. kula builds its
      // own components map and spreads `reactSelectProps` last, so a `components`
      // key there replaces the whole map — taking the Control that draws the
      // "To" label and the MultiValue that draws the chips with it.
      componentsOverride: { Menu: RecipientPickerMenu },
      // kula's own chip has one shape for every value; users, job roles and
      // Kula roles each need their own mark. `renderTag` is its supported
      // seam, so the Control and the rest of the chip row stay kula's.
      renderTag: (
        option: RecipientChipOption,
        api: { onRemove: () => void }
      ) => (
        <RecipientChip
          // The roles come off what the picker rendered, not off the tag kula
          // hands back — a chip rebuilt from a saved activity never passed
          // through an option that carried them.
          option={{
            ...option,
            tags:
              seenRef.current.get(String(option.value))?.tags ?? option.tags,
          }}
          onRemove={api.onRemove}
        />
      ),
      // `onSearchChange`, NOT `reactSelectProps.onInputChange`. kula drives
      // react-select's `inputValue` from its own state and updates it inside
      // its `onInputChange`, then calls this. Passing one through
      // `reactSelectProps` replaces that handler outright — kula's state never
      // advances, so the field stays empty and nothing is ever searched.
      onSearchChange: (value: string) => setQuery(value),
      reactSelectProps: {
        // Where the menu portals is the caller's call. Left undefined, kula sends
        // it to `document.body` — right on a Chakra surface, but dead inside a
        // kula Drawer: that is a Radix Dialog with `modal` on, so everything
        // outside the dialog gets `pointer-events: none` (no clicks, no scroll)
        // and the panel stacks above it at z-index 1500. Such a caller passes a
        // node inside the drawer instead, keeping the menu floating but in-dialog.
        ...(menuPortalTarget === undefined ? {} : { menuPortalTarget }),
        // The menu renders the sections itself, so react-select's own filtering
        // and empty-state would only fight it.
        filterOption: () => true,
        /**
         * Measure the panel against the window, not against the drawer.
         *
         * The panel is ~420px tall and opens against the field it belongs to.
         * Fill To with enough chips and that row grows tall enough to push Cc
         * most of the way down the drawer, and the panel then runs off the
         * bottom of the screen — taking Add with it, which is the one control it
         * cannot do without.
         *
         * kula already asks for `menuPlacement: 'auto'`, so react-select is
         * willing to flip or shrink the panel. What stopped it is what it
         * measures: with the default `position: absolute` it sizes against the
         * menu's scroll parent, and here that is the drawer body — 870px tall
         * with 1246px of content, so there always looked to be room below. It
         * would then offer to scroll the drawer to reveal the panel, which this
         * picker refuses (`closeMenuOnScroll`), leaving the panel hanging off
         * the screen instead.
         *
         * Fixed positioning makes `window.innerHeight` the measure, so
         * react-select shrinks the panel to the space that genuinely exists
         * below the field — and flips it above only when even that runs out.
         * Safe here because nothing between the menu and the body is
         * transformed, which is the one thing that would re-anchor `fixed`.
         */
        menuPosition: 'fixed' as const,
        // The budget react-select hands back once it has measured. Left at its
        // own 300 default, a panel constrained to fit would be sized against a
        // height this one never had.
        maxMenuHeight: RECIPIENT_MENU_MAX_HEIGHT,
        // And the floor it shrinks to before flipping instead — this panel's
        // own, so the two agree on when there is no longer room below.
        minMenuHeight: RECIPIENT_MENU_MIN_HEIGHT,
        /**
         * The panel floats: it is portalled and positioned once, against where the
         * field was. Scrolling the drawer under it left it hanging over unrelated
         * rows, so it closes instead — react-select's own seam for exactly this.
         *
         * Not every scroll, though: the users section pages as it scrolls, and
         * those events bubble to here too. A scroll that started inside the menu
         * is the menu's own business.
         */
        closeMenuOnScroll: (event: Event) => {
          const target = event.target as { closest?: (_s: string) => unknown };
          if (typeof target?.closest !== 'function') return true;
          return !target.closest(`[data-test-id="${MENU_TEST_ID}"]`);
        },
        /**
         * CORE-5433. This picker renders its own menu, so react-select's keyboard
         * focus is invisible: no row is highlighted and nothing says a key will
         * commit anything. Its focus still starts on the first option of the
         * flattened list, which is "Primary hiring managers (on job)" — so a
         * search for a person, then Enter, added that role instead.
         *
         * `preventDefault` rather than a swallowed event: react-select calls this
         * first and bails the moment the event is default-prevented, and the same
         * call stops the browser submitting the form the field sits in.
         *
         * Rows are ticked by clicking them and committed with Add. That is the
         * only way in, and now the only way in.
         */
        onKeyDown: (event: { key: string; preventDefault: () => void }) => {
          if (event.key === 'Enter') event.preventDefault();
        },
        // The same defect one key over: with the menu open, Tab commits that same
        // unseen option. Off, so Tab does what it should here and moves on.
        tabSelectsValue: false,
        // Read by the menu off `selectProps`.
        recipientPicker: {
          sections,
          optionByValue: seenRef.current,
          // The menu owns the scroll container, so it is what notices the end of
          // the list; the hook owns the paging.
          onEndReached: users.loadMore,
        },
      },
    }),
    [sections, menuPortalTarget, users.loadMore]
  );

  /**
   * What each row weighs, built from the UNFILTERED role lists.
   *
   * Not from `sections`: those are narrowed by the search box and the users
   * section is paged, so a role that is selected but filtered out of view would
   * lose its weight and the budget would jump as someone typed.
   */
  const costModel = useMemo(
    () => buildCostModel([...jobRoleOptions, ...orgRoleOptions]),
    [jobRoleOptions, orgRoleOptions]
  );

  /**
   * The same props, told which field they are for and what the field sharing its
   * budget already holds.
   *
   * Cc and Bcc are two react-selects spending one 450 and neither can see the
   * other — only the composer holds both values, so it is the one that has to
   * say. To spends its own 200 and has no sibling.
   */
  const selectPropsFor = useCallback(
    (field: RecipientField, selected: RecipientSelection) => {
      if (!enforceLimits) return selectProps;

      const limit =
        field === 'to'
          ? (limits.to ?? MAX_TO_RECIPIENTS)
          : (limits.ccBcc ?? MAX_CC_BCC_RECIPIENTS);
      const otherFieldValues =
        field === 'cc' ? selected.bcc : field === 'bcc' ? selected.cc : [];

      return {
        ...selectProps,
        reactSelectProps: {
          ...selectProps.reactSelectProps,
          recipientPicker: {
            ...selectProps.reactSelectProps.recipientPicker,
            costModel,
            limit,
            otherFieldValues,
          },
        },
      };
    },
    [enforceLimits, selectProps, costModel, limits.to, limits.ccBcc]
  );

  return {
    selectProps,
    selectPropsFor,
    sections,
    isSearching: users.isFetching,
  };
};
